/**
 * Phase 48 — `studio_secrets` kind taxonomy.
 *
 * Replaces the AES-256-GCM `secrets.ts` module deleted in the same
 * phase commit. Lists the known vendor-credential kinds Layer's
 * connectors read from `studio_secrets.value` (plaintext, RLS-isolated,
 * Supabase disk-encrypted; `SERVER_REDACTED_COLUMNS["studio_secrets"]
 * = ['value']` keeps the column off the `/data/studio_secrets` browser).
 *
 * Locked sub-decision H (phase 48 brief). Single source of truth for:
 *
 *   - the chokepoint's `edit_studio_secrets` + `reveal_studio_secret`
 *     + `list_studio_secrets_presence` actions (unknown kind → 400
 *     `secret_kind_unknown` at the implementation layer)
 *   - the dashboard's `/config/:slug` section #9 "Vendor credentials"
 *     editor (renders one form row per kind in this exact order)
 *   - the CLI `secrets set --kind` allowlist (mirrors the same set;
 *     previously a hand-maintained list in `secrets-set.ts`)
 *
 * Order is human-grouped by vendor (stripe first, klaviyo second,
 * resend third, whatsapp fourth) and stable across
 * phases — operators bookmark Reveal-button positions; reordering
 * shifts the UI.
 *
 * Phase 48 shipped eleven kinds; the array has grown since — `KNOWN_SECRET_KINDS.length` is the live count. Adding a kind in a future phase: append
 * to this array + register the kind label in the dashboard's section #9
 * + add a CLI allowlist entry (the const is the single source).
 */

/**
 * Every recognised `studio_secrets.kind` value. Strict `readonly`
 * tuple so TypeScript narrows accepted strings at the call site.
 */
export const KNOWN_SECRET_KINDS = [
  // ----- Stripe (3) -----
  "stripe.webhook_signing_secret",
  // 2026-05-31 — second Stripe webhook signing secret for a SECOND
  // Stripe account feeding the same `/webhooks/stripe/<slug>` endpoint.
  // E-Pulsive's real CodexFit subscriptions live on a different Stripe
  // account (`acct_18txST…`) than the test/CRM-V1 account (`acct_1SRJsU…`).
  // Both POST to the one receiver endpoint; the connector verifies the
  // incoming signature against EITHER secret (try-both). The test secret
  // stays under `stripe.webhook_signing_secret`; this kind holds the live
  // CodexFit account's `whsec_…`.
  "stripe.webhook_signing_secret_live",
  "stripe.api_key",
  // 2026-07-27 — the LIVE account's outbound API key, added when Stripe was
  // split into two independently-switchable connectors (stripe_live /
  // stripe_test). `stripe.api_key` above stays the TEST/sandbox key so the
  // existing rows keep working; which one an outbound call uses is chosen
  // EXPLICITLY by `stripe.outbound_environment` in the L2 config, never
  // inferred from which key happens to be present.
  "stripe.api_key_live",
  // ----- Klaviyo (1) -----
  "klaviyo.webhook_bearer_token",
  // ----- Resend (2) -----
  "resend.api_key",
  "resend.webhook_signing_secret",
  // ----- WhatsApp (4) -----
  "whatsapp.access_token",
  "whatsapp.app_secret",
  "whatsapp.phone_number_id",
  "whatsapp.verify_token",
  // ----- Telegram (1) — 2026-07-06 -----
  // Bot token for Layer's internal-ops Telegram channel (PA + staff
  // alerts → management). Env fallback: LAYER_TELEGRAM_BOT_TOKEN.
  "telegram.bot_token",
  // ----- Instagram (5) — 2026-07-06 / DM reply 2026-07-08 -----
  // Long-lived Meta Graph token + the connected IG Business account id
  // for `post_instagram`. Env fallbacks: LAYER_INSTAGRAM_ACCESS_TOKEN /
  // LAYER_INSTAGRAM_BUSINESS_ACCOUNT_ID.
  "instagram.access_token",
  "instagram.business_account_id",
  // 2026-07-08 — Instagram DM reply subsystem. A dedicated Page token for
  // the Messaging API (`instagram_manage_messages`) — OPTIONAL: the DM
  // resolver falls back to `instagram.access_token` when this is absent
  // (the shared @epulsive Page token already carries manage_messages).
  "instagram.dm_access_token",
  // Meta app secret for verifying the IG `messages` webhook HMAC. OPTIONAL:
  // WhatsApp + Instagram sit on the SAME Meta app, so the webhook resolver
  // falls back to `whatsapp.app_secret` when this is absent.
  "instagram.app_secret",
  // GET-handshake verify token for the IG `messages` webhook subscription
  // (a value we mint and enter in the Meta app console). Env fallback:
  // LAYER_INSTAGRAM_VERIFY_TOKEN.
  "instagram.verify_token",
  // ----- Gmail (1) — 2026-07-08 -----
  // Google Workspace service-account key JSON (domain-wide delegation) for
  // the Gmail connector — sends + scans mail AS `gmail.impersonate_user`
  // (a real Workspace mailbox, e.g. contact@e-pulsive.co.uk). Per-studio
  // credential; NO env fallback (unlike telegram/instagram).
  "gmail.service_account_key",
  // ----- Website analytics (3) — 2026-07-24 -----
  // Read-side credentials for the `website_analytics_daily` cron saga
  // (dashboard `/analytics` page). All three are per-studio, NO env
  // fallback. Non-secret ids (pixel id, Clarity project id, GA4 property
  // id, hostnames) live in the L2 `website_analytics` config block.
  //
  // Long-lived Meta Marketing/Graph user token used READ-ONLY for pixel
  // stats. 60-day expiry — refreshed together with the other holders of
  // the same token (AgentFlow/.env + the website's CAPI bundle).
  "meta.ads_access_token",
  // Microsoft Clarity Data Export API token (project settings → Data
  // Export). Budget: 10 requests/project/day — the daily saga spends ≤5.
  "clarity.api_token",
  // Google Cloud service-account key JSON with the Analytics Data API
  // enabled + the SA granted Viewer on the GA4 property. Same RS256
  // JWT technique as `gmail.service_account_key`, different scope
  // (analytics.readonly) and usually a different service account.
  "ga4.service_account_key",
  // ----- WhatsApp coexistence (1) — 2026-08-25 -----
  // The WABA id minted by Embedded Signup onboarding (the coexistence
  // flow's FINISH event carries it; the /whatsapp-signup exchange
  // endpoint stores it). Non-secret, co-located with the whatsapp.*
  // credentials for unified per-studio management — same posture as
  // `whatsapp.phone_number_id` above. Read by onboarding-day tooling
  // (template resubmission targets the WABA), not by the send path.
  "whatsapp.waba_id",
  // ----- WhatsApp coexistence (2) — 2026-08-25 -----
  // The Embedded Signup completion PARKS the newly-minted business token
  // + resolved phone_number_id under these pending kinds instead of the
  // live `whatsapp.access_token` / `whatsapp.phone_number_id` rows: the
  // send-path resolvers SELECT the live kinds per call, so writing them
  // directly would repoint production onto a WABA whose message
  // templates are not yet approved (runbook §7 ordering). The explicit
  // Activate step on /whatsapp-signup promotes pending → live and
  // deletes these rows in one edit_studio_secrets dispatch.
  "whatsapp.pending_access_token",
  "whatsapp.pending_phone_number_id",
] as const;

/** Type-narrowed union of the known kinds (compile-time check). */
export type KnownSecretKind = (typeof KNOWN_SECRET_KINDS)[number];

/**
 * Read-only `Set` view for cheap membership tests. The `Set<string>`
 * type is intentional so callers can pass arbitrary strings (the
 * implementation raises `secret_kind_unknown` on misses).
 */
export const KNOWN_SECRET_KINDS_SET: ReadonlySet<string> = new Set(
  KNOWN_SECRET_KINDS,
);

/** True iff `kind` is one of the recognised secret-kind labels. */
export function isKnownSecretKind(kind: string): kind is KnownSecretKind {
  return KNOWN_SECRET_KINDS_SET.has(kind);
}

/**
 * Human-readable label per kind. The dashboard's section #9 uses these
 * as the form-row label so operators don't have to mentally translate
 * the snake_case key. Defaults preserve the kind itself for any future
 * kind that lacks an entry (forward-compat for an added kind that
 * forgot a label).
 */
export const KNOWN_SECRET_KIND_LABELS: Readonly<
  Record<KnownSecretKind, string>
> = {
  "stripe.webhook_signing_secret": "Stripe · Webhook signing secret",
  "stripe.webhook_signing_secret_live":
    "Stripe · Webhook signing secret (live CodexFit account)",
  "stripe.api_key": "Stripe · API key (test / sandbox account)",
  "stripe.api_key_live": "Stripe · API key (live CodexFit account)",
  "klaviyo.webhook_bearer_token": "Klaviyo · Webhook bearer token",
  "resend.api_key": "Resend · API key",
  "resend.webhook_signing_secret": "Resend · Webhook signing secret",
  "whatsapp.access_token": "WhatsApp · Access token",
  "whatsapp.app_secret": "WhatsApp · App secret",
  "whatsapp.phone_number_id": "WhatsApp · Phone number ID",
  "whatsapp.verify_token": "WhatsApp · Verify token",
  "telegram.bot_token": "Telegram · Bot token (PA + staff alerts)",
  "instagram.access_token": "Instagram · Access token (Graph API)",
  "instagram.business_account_id": "Instagram · Business account id",
  "instagram.dm_access_token":
    "Instagram · DM access token (Messaging API — optional, falls back to Access token)",
  "instagram.app_secret":
    "Instagram · App secret (webhook HMAC — optional, falls back to WhatsApp app secret)",
  "instagram.verify_token": "Instagram · Webhook verify token",
  "gmail.service_account_key":
    "Gmail · Service-account key (Workspace domain-wide delegation)",
  "meta.ads_access_token":
    "Meta · Ads/Graph access token (pixel stats, read-only — 60-day)",
  "clarity.api_token": "Clarity · Data Export API token",
  "ga4.service_account_key":
    "GA4 · Service-account key (Analytics Data API, read-only)",
  "whatsapp.waba_id": "WhatsApp · WABA id (Embedded Signup)",
  "whatsapp.pending_access_token":
    "WhatsApp · PENDING access token (Embedded Signup, pre-activation)",
  "whatsapp.pending_phone_number_id":
    "WhatsApp · PENDING phone number id (Embedded Signup, pre-activation)",
};

/**
 * Stable wire-error class for the unknown-kind rejection path. Surfaces
 * on the chokepoint's `mapDispatchErrorToStatus` table as a NEW explicit
 * case `secret_kind_unknown → 400` (the bare `_unknown` suffix has no
 * suffix rule in the dispatch table).
 */
export class SecretKindUnknownError extends Error {
  readonly code = "secret_kind_unknown";
  constructor(kind: string) {
    super(
      `secret_kind_unknown: '${kind}' is not in KNOWN_SECRET_KINDS (${KNOWN_SECRET_KINDS.length} kinds: ${KNOWN_SECRET_KINDS.join(", ")})`,
    );
    this.name = "SecretKindUnknownError";
  }
}
